import { useEffect, useState } from "react";
import { checkIn, createHabit, deleteHabit, listHabits, uncheckIn, updateHabit } from "./HabitsService";
import { useToast } from "../ToastContext";
import { useApiCall } from "../useApiCall";
import "./HabitsPage.css";

const FREQ_OPTIONS = ["DAILY", "WEEKLY"];
const CAT_OPTIONS  = ["HEALTH", "PERSONAL", "WORK", "LEARNING", "SOCIAL", "FITNESS", "OTHER"];

export default function HabitsPage() {
  const toast = useToast();
  const { execute, loading } = useApiCall();
  const [habits, setHabits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await execute(listHabits);
    if (data) setHabits(data);
  };

  const [checkInNote, setCheckInNote] = useState({ id: null, text: "" });

  const handleCheckIn = async (id, note = "") => {
    const updated = await execute(() => checkIn(id, note));
    if (updated) {
      setHabits((prev) => prev.map((h) => (h.id === id ? updated : h)));
      setCheckInNote({ id: null, text: "" });
      toast("Checked in!", "success");
    }
  };

  const handleUncheck = async (id) => {
    const updated = await execute(() => uncheckIn(id));
    if (updated) {
      setHabits((prev) => prev.map((h) => (h.id === id ? updated : h)));
      toast("Check-in removed", "info");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete habit "${name}"?`)) return;
    await execute(() => deleteHabit(id));
    setHabits((prev) => prev.filter((h) => h.id !== id));
    toast("Habit deleted", "info");
  };

  const handleSave = async (formData) => {
    if (editing) {
      const updated = await execute(() => updateHabit(editing.id, formData));
      if (updated) {
        setHabits((prev) => prev.map((h) => (h.id === editing.id ? updated : h)));
        toast("Habit updated", "success");
      }
    } else {
      const created = await execute(() => createHabit(formData));
      if (created) {
        setHabits((prev) => [created, ...prev]);
        toast("Habit created", "success");
      }
    }
    setShowForm(false);
    setEditing(null);
  };

  const active   = habits.filter((h) => h.active);
  const inactive = habits.filter((h) => !h.active);

  return (
    <div className="habits-page">
      <div className="habits-header">
        <h2>Habits</h2>
        <button className="habits-new-btn" onClick={() => { setEditing(null); setShowForm(true); }}>
          + New Habit
        </button>
      </div>

      {showForm && (
        <HabitForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {loading && <div className="habits-loading">Loading...</div>}

      {!loading && active.length === 0 && !showForm && (
        <div className="habits-empty">No active habits. Click "+ New Habit" to start tracking.</div>
      )}

      <div className="habits-list">
        {active.map((h) => (
          <HabitCard
            key={h.id}
            habit={h}
            checkInNote={checkInNote}
            onStartCheckIn={() => setCheckInNote({ id: h.id, text: "" })}
            onCheckIn={(note) => handleCheckIn(h.id, note)}
            onCancelCheckIn={() => setCheckInNote({ id: null, text: "" })}
            onNoteChange={(text) => setCheckInNote(prev => ({ ...prev, text }))}
            onUncheck={() => handleUncheck(h.id)}
            onEdit={() => { setEditing(h); setShowForm(true); }}
            onDelete={() => handleDelete(h.id, h.name)}
          />
        ))}
      </div>

      {inactive.length > 0 && (
        <details className="habits-inactive-section">
          <summary>Paused habits ({inactive.length})</summary>
          <div className="habits-list" style={{ marginTop: 12 }}>
            {inactive.map((h) => (
              <HabitCard
                key={h.id}
                habit={h}
                onEdit={() => { setEditing(h); setShowForm(true); }}
                onDelete={() => handleDelete(h.id, h.name)}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function HabitCard({ habit, checkInNote, onStartCheckIn, onCheckIn, onCancelCheckIn, onNoteChange, onUncheck, onEdit, onDelete }) {
  const isExpanded = checkInNote?.id === habit.id;
  const doneToday = habit.completedToday;
  const streak    = habit.currentStreak || 0;
  const best      = habit.longestStreak || 0;
  const last30    = habit.last30Days || [];

  // Build a 28-day grid (4 weeks)
  const today = new Date();
  const grid  = Array.from({ length: 28 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (27 - i));
    const iso = d.toISOString().slice(0, 10);
    return { date: iso, done: last30.includes(iso) };
  });

  return (
    <div className={`habit-card ${!habit.active ? "habit-card-inactive" : ""}`}>
      <div className="habit-card-top">
        <div>
          <span className="habit-name">{habit.name}</span>
          {habit.category && <span className="habit-cat-chip">{habit.category}</span>}
          <span className="habit-freq-chip">{habit.frequency}</span>
        </div>
        <div className="habit-streak-box">
          <span className="habit-streak-cur" title="Current streak">{streak} day{streak !== 1 ? "s" : ""}</span>
          <span className="habit-streak-best" title="Best streak">Best: {best}</span>
        </div>
      </div>

      {habit.description && (
        <div className="habit-description">{habit.description}</div>
      )}

      {/* 28-day heatmap */}
      <div className="habit-heatmap">
        {grid.map((cell) => (
          <div
            key={cell.date}
            className={`habit-heatmap-cell ${cell.done ? "habit-heatmap-done" : ""}`}
            title={cell.date}
          />
        ))}
      </div>

      <div className="habit-card-actions">
        {habit.active && (
          doneToday ? (
            <button className="habit-btn habit-btn-uncheck" onClick={onUncheck}>
              Undo today
            </button>
          ) : isExpanded ? (
            <div className="habit-checkin-note-row">
              <input
                autoFocus
                className="habit-checkin-note-input"
                value={checkInNote.text}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="Add a note (optional)…"
                onKeyDown={e => e.key === "Enter" && onCheckIn(checkInNote.text)}
              />
              <button className="habit-btn habit-btn-checkin" onClick={() => onCheckIn(checkInNote.text)}>
                ✓ Done
              </button>
              <button className="habit-btn" onClick={onCancelCheckIn}>✕</button>
            </div>
          ) : (
            <button className="habit-btn habit-btn-checkin" onClick={onStartCheckIn}>
              Check in today
            </button>
          )
        )}
        {!isExpanded && (
          <>
            <button className="habit-btn" onClick={onEdit}>Edit</button>
            <button className="habit-btn habit-btn-delete" onClick={onDelete}>Delete</button>
          </>
        )}
      </div>
    </div>
  );
}

function HabitForm({ initial, onSave, onCancel }) {
  const [name,      setName]      = useState(initial?.name      || "");
  const [desc,      setDesc]      = useState(initial?.description || "");
  const [freq,      setFreq]      = useState(initial?.frequency || "DAILY");
  const [category,  setCategory]  = useState(initial?.category  || "HEALTH");
  const [startDate, setStartDate] = useState(initial?.startDate || "");
  const [active,    setActive]    = useState(initial?.active !== false);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: desc, frequency: freq, category, startDate: startDate || null, active });
  };

  return (
    <form className="habit-form" onSubmit={submit}>
      <h3>{initial ? "Edit Habit" : "New Habit"}</h3>
      <div className="habit-form-row">
        <label>Name *
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Morning run" />
        </label>
        <label>Frequency
          <select value={freq} onChange={(e) => setFreq(e.target.value)}>
            {FREQ_OPTIONS.map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
      </div>
      <div className="habit-form-row">
        <label>Category
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CAT_OPTIONS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Start Date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
      </div>
      <label>Description
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Optional notes..." />
      </label>
      <label className="habit-form-check">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      <div className="habit-form-actions">
        <button type="submit" className="habit-form-save">Save</button>
        <button type="button" className="habit-form-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
